import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { styled, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Paper } from '@openx/components-core/lib/Paper/Paper.tsx';
import type { DealTroubleshoot } from '@openx/types/deal.ts';
import { bigNumberWithCommas } from '@openx/utils/lib/bigNumberWithCommas.ts';
import { formatChartXTick, formatChartYTick } from '@openx/utils/lib/chartFunctions.ts';
import { useCallback } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const StyledContainerPaper = styled(Paper)`
  padding: ${({ theme }) => theme.spacing(4)};
`;

const StyledChartHeaderGrid = styled(Grid)`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const StyledTitleWrapperGrid = styled(Grid)`
  width: fit-content;
`;

const StyledCaptionTypography = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  margin-left: ${({ theme }) => theme.spacing(1.5)};
`;

const StyledTooltipContainerDiv = styled('div')<{ color: string }>`
  background: ${({ color }) => color};
  border-radius: 2px;
  color: ${({ theme }) => theme.palette.primary.contrastText};
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
`;

export interface TroubleshootingChartProps {
  title: string;
  data: DealTroubleshoot[];
  metric: string;
  color: string;
  caption: string;
  isHourlyData?: boolean;
}

export function TroubleshootingChart(props: TroubleshootingChartProps) {
  const { title, data, metric, color, caption, isHourlyData = false } = props;
  const theme = useTheme<Theme>();

  const renderTooltip = useCallback(
    props => {
      const { payload } = props;

      if (!payload.length) return null;

      const value = payload[0].value;
      // Round bid_cpm to 2 decimal places
      const formattedValue =
        metric === 'stats.bid_cpm' && typeof value === 'number' ? value.toFixed(2) : bigNumberWithCommas(value);

      return (
        <StyledTooltipContainerDiv color={color}>
          <Typography variant="body2">{formattedValue}</Typography>
        </StyledTooltipContainerDiv>
      );
    },
    [color, metric]
  );

  const formatTick = useCallback((date: string) => formatChartXTick(date, isHourlyData), [isHourlyData]);

  return (
    <>
      <StyledContainerPaper data-test={`${metric}-chart`}>
        <StyledChartHeaderGrid container wrap="nowrap" sx={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <StyledTitleWrapperGrid container sx={{ alignContent: 'center', alignItems: 'flex-end' }}>
            <Typography variant="h2" data-test={`${metric}-title`}>
              {title}
            </Typography>
            <StyledCaptionTypography variant="body2" color="textSecondary" data-test={`${metric}-date`}>
              {caption}
            </StyledCaptionTypography>
          </StyledTitleWrapperGrid>
        </StyledChartHeaderGrid>
        <ResponsiveContainer height={140} width="100%">
          <LineChart data={data} syncId="troubleshooting-charts" margin={{ top: 5, right: 20, bottom: 15, left: 5 }}>
            <CartesianGrid stroke={theme.palette.divider} />
            <XAxis
              dataKey="timestamp"
              axisLine={false}
              tickLine={false}
              tickSize={10}
              tickFormatter={formatTick}
              interval={data.length > 30 ? 2 : 0}
              angle={-45}
              dy={10}
              dx={-5}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickSize={10}
              width={40}
              allowDecimals={false}
              tickFormatter={formatChartYTick}
            />
            <Tooltip cursor={{ stroke: theme.palette.action.disabled }} content={renderTooltip} />
            <Line type="monotone" dataKey={metric} stroke={color} activeDot={{ r: 5.5 }} />
          </LineChart>
        </ResponsiveContainer>
      </StyledContainerPaper>
      <Divider />
    </>
  );
}
